import { app, pages } from '@/config/index.js'
import { useCommonStore } from '@/stores/common.js'
import { useMemberStore } from '@/stores/member.js'
import { useAuthStore } from '@/stores/auth.js'
import { isApp } from '@/utils/browser.js'
import { valueOrDefault } from '@/utils/common.js'
import { isMobile } from '@/utils/responsive.js'
import { getLocalStorageData } from '@/utils/storage.js'

function redirectionPath (to, redirectionPath) {
  return {
    path: redirectionPath,
    params: to.params,
    query: {
      ...to.query,
      appsWebview: true
    }
  }
}

function hasAppsWebview (to) {
  return to.query.appsWebview
}

export function redirectWithAppsWebView (to, next) {
  hasAppsWebview(to) ? next() : next(redirectionPath(to, to.path))
}

function getMemberSuccess (to, next) {
  const authStore = useAuthStore()
  // don't clear recovery state if THIS page instance just triggered a bridge
  // redirect that's still pending — an unrelated, overlapping navigation's
  // success here must not re-arm recovery that hasn't resolved yet
  authStore.clearRecoveryIfUnowned()
  validateOnboardingPage(to).then(onboardingRedirection => {
    if (onboardingRedirection) {
      next(onboardingRedirection)
    } else if (to.path.startsWith(pages.registration)) {
      next(redirectionPath(to, pages.home))
    } else {
      redirectWithAppsWebView(to, next)
    }
  })
}

function getMemberFail (to, next) {
  const memberStore = useMemberStore()
  const { isNotLoggedIn } = memberStore

  if (!isNotLoggedIn) {
    redirectToRegistration(to, next)
    return
  }

  // a 401 here (isNotLoggedIn) is already being handled by http-api.js's
  // defaultHandlerConnectionError via the same underlying getMember() request.
  // On the web, that flow (refreshToken -> reload()/redirectToLogin()) always
  // navigates the whole page away, so next() intentionally stays uncalled —
  // unchanged from before.
  if (!isApp()) return

  // in-app: recovery is now a bounded state machine (see useAuthStore) — wait
  // for it to settle (recovered, or failed/timed out) instead of leaving this
  // navigation pending forever
  const authStore = useAuthStore()
  authStore.waitForRecoveryToSettle().then((settledState) => {
    console.log('[AuthRecovery] router guard resumed after recovery settled', { settledState })
    next()
  })
}

export function redirectToRegistration (to, next) {
  !to.path.startsWith(pages.registration)
    ? next(redirectionPath(to, pages.registration))
    : redirectWithAppsWebView(to, next)
}

export function checkAffiliateMember (to, next) {
  const memberStore = useMemberStore()
  const { getMember } = memberStore

  getMember({
    success: () => { getMemberSuccess(to, next) },
    fail: () => { getMemberFail(to, next) }
  })
}

export function validateOnboardingPage (to) {
  return new Promise(resolve => {
    const commonStore = useCommonStore()
    const memberStore = useMemberStore()
    const { affiliateConfigs } = commonStore
    const { member } = memberStore

    if (affiliateConfigs.init || member.init) {
      setTimeout(() => {
        resolve(validateOnboardingPage(to))
      }, 50)
      return
    }

    const isNotFoundPage = app.routerName.notFound === to.name
    if (!isMobile() || isNotFoundPage) {
      resolve(null)
      return
    }

    const excludedPrefixes = [pages.registration, pages.onboarding]
    const isExcluded = excludedPrefixes.some(prefix => to.path.startsWith(prefix))

    const onboardingCompleted = member?.uapOnboardingCompleted === true

    if (!isExcluded && !onboardingCompleted) {
      resolve({
        path: pages.onboarding,
        query: { redirect: encodeURIComponent(to.fullPath) }
      })
    }

    resolve(null)
  })
}